<template>
  <div class="select-container">
    <div class="select-header">
      <div
        class="close"
        @click="close()"
      >
        <img
          class="close-img"
          src="@/assets/icons/arrow-left.svg"
          alt="close"
        >
      </div>
      <span>{{ name }}</span>
    </div>
    <div class="data-container">
      <div class="input-wrapper">
        <input
          v-if="searchbar"
          ref="search"
          v-model="searchTerm"
          type="text"
          class="input-search"
          :placeholder="$t('form.search')"
          @keydown.enter.prevent="close()"
        >
        <img 
          v-if="searchTerm"
          src="@/assets/img/cancel.svg"
          alt="Clear search"
          class="clear-icon"
          @click="clearSearch()"
        >
      </div>
      <div
        class="options"
        :class="searchbar ? 'options-with-search' : ''"
      >
        <template v-if="!multiple">
          <label
            v-for="option in filteredOptions"
            :key="option"
            class="label"
          >
            <span>{{ generateLabel(option) }}</span>
            <input
              v-model="selectedOption"
              type="radio"
              :value="option"
              @change="emitSelectedOptions()"
            >
          </label>
        </template>
        <template v-else>
          <label
            v-for="(option, index) in filteredOptions"
            :key="index"
            class="label"
          >
            <span>{{ generateLabel(option) }}</span>
            <input
              v-model="selectedOptions"
              type="checkbox"
              :value="option"
              :disabled="maxSelectionReached && !selectedOptions.includes(option)"
              @change="emitSelectedOptions()"
            >
          </label>
        </template>
        <template v-if="!filteredOptions.length">
          <span class="no-result">
            {{ $t('errors.datas_not_found') }}
          </span>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FullScreenSelect',
  props: {
    options: {
      type: Array,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    maxSelection: {
      type: Number,
      default: 0
    },
    searchbar: {
      type: Boolean,
      default: false
    },
    labelFields: {
      type: Array,
      default: null
    },
    defaultValue: [String, Array, Object],
    parentType: {
      type: String,
      default: null
    },
    parentTranslationKey: {
      type: String,
      default: 'form'
    }
  },
  emits: ['selected-option', 'fs-select-close'],
  data() {
    return {
      show: false,
      selectedOption: this.defaultValue,
      selectedOptions: this.defaultValue,
      searchTerm: '',
      windowHeight: window.innerHeight
    }
  },
  computed: {
    filteredOptions() {
      let searchTerm = this.searchTerm.trim()
      let regex = new RegExp(searchTerm.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&'), 'i')
      if (this.labelFields && this.areLabelFieldsInOptions) {
        return this.options.filter(option => {
          return this.labelFields.some(field => regex.test(option[field]))
        })
      } else {
        if (this.options.every(option => option.hasOwnProperty('label'))) {
          return this.options.filter(option => regex.test(option.label))
        } else {
          return this.options.filter(option => {
            return regex.test(this.generateLabel(option))
          })
        }
      }
    },
    areLabelFieldsInOptions() {
      if (this.labelFields) {
        return this.options.some(option => {
          return this.labelFields.some(key => key in option)
        })
      }
      return false
    },
    maxSelectionReached() {
      if (this.maxSelection == 0) {
        return false
      } else {
        return this.selectedOptions.length >= this.maxSelection
      }
    }
  },
  mounted() {
    if (this.parentType == 'CustomPhoneInput') {
      if (typeof this.defaultValue !== 'object' && this.defaultValue !== null) {
        this.selectedOption = this.options.find(option => option.dial_code === this.defaultValue)
      }
    }
    if (this.parentType == 'CustomCountrySelect') {
      if (typeof this.defaultValue !== 'object' && this.defaultValue !== null) {
        this.selectedOption = this.options.find(option => option.value === this.defaultValue)
      }
    }
  },
  methods: {
    generateLabel(obj) {
      let label = ''
      if (this.labelFields && this.areLabelFieldsInOptions) {
        this.labelFields.forEach(field => {
          if (label === '') {
            label = obj[field]
          } else {
            label = label + ' ' + obj[field]
          }
        })
        return this.$te(this.parentTranslationKey + '.' + label)
          ? this.$t(this.parentTranslationKey + '.' + label)
          : label
      } else {
        if (obj.label) {
          return this.$te(this.parentTranslationKey + '.' + obj.label)
            ? this.$t(this.parentTranslationKey + '.' + obj.label)
            : obj.label
        } else {
          return this.$te(this.parentTranslationKey + '.' + obj)
            ? this.$t(this.parentTranslationKey + '.' + obj)
            : obj
        }
      }
    },
    emitSelectedOptions() {
      if (this.parentType == 'CustomCountrySelect') {
        this.$emit('selected-option', this.selectedOption.value)
        this.close()
      } else {
        if (this.multiple) {
          this.$emit('selected-option', this.selectedOptions)
        } else {
          this.$emit('selected-option', this.selectedOption)
          this.close()
        }
      }
    },
    clearSearch() {
      this.searchTerm = ''
      this.$refs.search.focus()
    },
    open() {
      this.show = true
    },
    close() {
      this.show = false
      this.$emit('fs-select-close')
    }
  }
}
</script>

<style lang="scss" scoped>
  .select-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 9999;
    background-color: rgb(250 250 250 / 100%);
    box-shadow: rgb(149 157 165 / 20%) 0 0 30px;

    .select-header {
      display: flex;
      align-items: center;
      min-height: 50px;
      box-shadow: rgb(149 157 165 / 20%) 0 0 5px;
      border-bottom: 1px solid rgb(0 0 0 / 10%);
      background-color: white;

      .close {
        margin-right: 15px;
        margin-left: 15px;
        width: 20px;
        cursor: pointer;
      }

      .close-img {
        width: 20px;
      }

      span {
        font-size: 1.3em !important;
        font-weight: bold;
        text-align: left;
      }
    }

    .data-container {
      padding: 0 20px;
      overflow-y: auto;
      max-height: calc(100%);
      margin-top: 10px;

      .input-wrapper {
        display: flex;

        .input-search {
          position: fixed;
          width: calc(100% - 45px);
          height: 25px;
          left: 0;
          padding: 1em;
          margin-left: 10px;
          background-color: white;
          border: none;
          box-shadow: rgb(149 157 165 / 20%) 0 0 5px;
          border-bottom: 1px solid rgb(0 0 0 / 10%);
          z-index: 5;
        }

        .clear-icon {
          position: relative;
          width: 0.8rem;
          height: 0.8rem;
          top: 13px;
          margin-left: auto;
          cursor: pointer;
          z-index: 10;
        }
      }
    }
  }

  .options {
    display: flex;
    flex-direction: column;
    text-align: left;
    margin-bottom: 100px;

    .label {
      padding: 15px 0;
      display: flex;
      justify-content: space-between;
      line-height: normal;
      white-space: pre-wrap;

      span {
        font-size: 1.4em !important;
      }
    }

    input[type='radio'],
    input[type='checkbox'] {
      margin-right: 5px;
      transform: scale(1.5);
    }

    input[type='checkbox']:checked {
      background-color: #7aff59;
    }

    .no-result {
      align-self: center;
      padding: 20px;
      font-size: 1.4em !important;
    }
  }

  .options-with-search {
    margin-top: 50px;
  }
</style>
